import React, { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMicrophone,
  faVideo,
  faDesktop,
  faPhone,
  faUsers,
  faBars,
  faComments,
  faKey,
  faArrowRight,
  faArrowLeft,
  faTimes,
  faLightbulb,
  faSlidersH,
  IconDefinition,
} from '@fortawesome/free-solid-svg-icons';
import { MEDIASFU_BRAND, brandSurface } from './brandPalette';

// ===============================================
// Mini Tour System - Modern React Implementation
// ===============================================

export interface TourStep {
  id: string;
  title: string;
  content: string;
  icon: IconDefinition;
  position: 'top' | 'bottom' | 'left' | 'right';
  targetRef?: React.RefObject<HTMLElement>;
  targetSelector?: string;
  action?: 'openMenu' | 'closeMenu';
  hostOnly?: boolean;
  inMenu?: boolean;
  /** Extra points shown as a short list under the main line. */
  bullets?: string[];
}

export interface MiniTourOptions {
  /** Whether the tour is visible */
  isVisible: boolean;
  /** Callback to close the tour */
  onClose: () => void;
  /** Whether the user is a host (islevel === "2") */
  isHost: boolean;
  /** Current dark mode state */
  isDarkMode: boolean;
  /** Event type (conference, webinar, broadcast, chat) */
  eventType?: string;
  /** Callback when menu should be opened */
  onOpenMenu?: () => void;
  /** Callback when menu should be closed */
  onCloseMenu?: () => void;
  /** Custom tour steps (optional) */
  customSteps?: TourStep[];
  /** Storage key for "don't show again" preference */
  storageKey?: string;
  /** Callback when tour completes */
  onComplete?: () => void;
}

// Cookie/localStorage helpers
const TOUR_STORAGE_KEY = 'mediasfu_tour_dismissed';

const getTourDismissed = (key: string = TOUR_STORAGE_KEY): boolean => {
  try {
    return localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
};

const setTourDismissed = (dismissed: boolean, key: string = TOUR_STORAGE_KEY): void => {
  try {
    localStorage.setItem(key, String(dismissed));
  } catch {
    // Silent fail if localStorage unavailable
  }
};

const CARD_WIDTH = 372;
const CARD_MARGIN = 14;
const SPOTLIGHT_PADDING = 8;

/**
 * Default tour steps.
 *
 * Kept deliberately short: a walkthrough people actually finish is worth more
 * than an exhaustive one they abandon. Menu features are grouped into single
 * steps rather than one dialog per item.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getDefaultTourSteps = (isHost: boolean, _eventType?: string): TourStep[] => {
  const steps: TourStep[] = [
    {
      id: 'mic',
      title: 'Your microphone',
      content: 'Tap to <strong>mute or unmute</strong>. Green means you are live, red means muted.',
      icon: faMicrophone,
      position: 'top',
      targetSelector: '[data-tour="mic-button"]',
    },
    {
      id: 'camera',
      title: 'Your camera',
      content: 'Toggle your <strong>camera</strong> on or off at any time.',
      icon: faVideo,
      position: 'top',
      targetSelector: '[data-tour="video-button"]',
    },
    {
      id: 'screenshare',
      title: 'Share your screen',
      content: 'Present a <strong>screen or single window</strong> to everyone in the room.',
      icon: faDesktop,
      position: 'top',
      targetSelector: '[data-tour="screenshare-button"]',
    },
    {
      id: 'participants',
      title: 'Who is here',
      content: 'See everyone in the meeting, who is speaking, and their current status.',
      icon: faUsers,
      position: 'top',
      targetSelector: '[data-tour="participants-button"]',
    },
    {
      id: 'chat',
      title: 'Chat',
      content: 'Message the whole room, or send a private message to one person.',
      icon: faComments,
      position: 'top',
      targetSelector: '[data-tour="chat-button"]',
      action: 'closeMenu',
    },
  ];

  // Everything behind the menu, grouped into one step per audience.
  steps.push({
    id: 'menu',
    title: 'Everything else lives here',
    content: 'The <strong>menu</strong> holds the rest of your controls.',
    icon: faBars,
    position: 'top',
    targetSelector: '[data-tour="menu-button"]',
    action: 'openMenu',
    bullets: isHost
      ? [
          'Recording, meeting settings, and participant permissions',
          'Requests, waiting room, and co-host assignment',
          'Breakout rooms, whiteboard, polls, and panelists',
          'Your Meeting ID to share with others',
        ]
      : [
          'Polls created by the host',
          'Media settings — camera, mic, speaker, virtual background',
          'Display settings for the video grid',
          'The Meeting ID to share with others',
        ],
  });

  steps.push({
    id: 'media',
    title: 'Tune your devices',
    content:
      'Under <strong>Media settings</strong> you can switch camera, microphone, or speaker, and turn on a virtual background.',
    icon: faSlidersH,
    position: 'top',
    targetSelector: '[data-tour="menu-button"]',
    inMenu: true,
  });

  if (isHost) {
    steps.push({
      id: 'hostPasscode',
      title: 'Keep your host passcode private',
      content:
        'Your <strong>host passcode</strong> grants full control of this meeting. Anyone who has it can act as host — share the Meeting ID instead.',
      icon: faKey,
      position: 'top',
      targetSelector: '[data-tour="menu-button"]',
      inMenu: true,
      hostOnly: true,
    });
  }

  steps.push({
    id: 'leave',
    title: 'Leaving the meeting',
    content: isHost
      ? 'Leave when you are done. As host you can also <strong>end the meeting for everyone</strong>.'
      : 'Click to <strong>leave</strong> the meeting whenever you are done.',
    icon: faPhone,
    position: 'top',
    targetSelector: '[data-tour="end-button"]',
    action: 'closeMenu',
  });

  return steps.filter(step => !step.hostOnly || isHost);
};

type CardPlacement = {
  top: number;
  left: number;
  /** Where the card ended up relative to the target, for the arrow. */
  side: 'top' | 'bottom' | 'left' | 'right' | 'center';
};

/**
 * MiniTour - A modern, themed tour component for MediaSFU
 */
export const MiniTour: React.FC<MiniTourOptions> = ({
  isVisible,
  onClose,
  isHost,
  isDarkMode,
  eventType = 'conference',
  onOpenMenu,
  onCloseMenu,
  customSteps,
  storageKey = TOUR_STORAGE_KEY,
  onComplete,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [cardSize, setCardSize] = useState({ width: CARD_WIDTH, height: 300 });
  const cardRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  // Get tour steps
  const tourSteps = useMemo(() => {
    return customSteps || getDefaultTourSteps(isHost, eventType);
  }, [customSteps, isHost, eventType]);

  const currentStepData = tourSteps[currentStep];
  const isLastStep = currentStep === tourSteps.length - 1;

  // Callers routinely pass inline arrows for these, so their identity changes
  // on every parent render. Holding them in refs keeps that churn out of the
  // effect below, which must fire once per step and not once per render.
  const onOpenMenuRef = useRef(onOpenMenu);
  const onCloseMenuRef = useRef(onCloseMenu);
  useEffect(() => {
    onOpenMenuRef.current = onOpenMenu;
    onCloseMenuRef.current = onCloseMenu;
  });

  // Handle step actions (open/close menu).
  //
  // Depends on the action itself rather than the step object or the callbacks:
  // with the callbacks in the dependency list this re-ran on every render of
  // the host component, and because opening the menu *toggles* it, the sidebar
  // flipped open and shut continuously — the whole UI shifting sideways each
  // time — instead of settling open.
  const stepAction = currentStepData?.action;
  useEffect(() => {
    if (!isVisible || !stepAction) return;
    if (stepAction === 'openMenu') {
      onOpenMenuRef.current?.();
    } else if (stepAction === 'closeMenu') {
      onCloseMenuRef.current?.();
    }
  }, [currentStep, isVisible, stepAction]);

  /**
   * Resolve the element this step points at and remember its box, so the card
   * can sit beside it and the spotlight can cut a hole around it. Steps with no
   * resolvable target fall back to a centered card.
   */
  const measureTarget = useCallback(() => {
    if (!isVisible || !currentStepData) {
      setTargetRect(null);
      return;
    }
    const el =
      currentStepData.targetRef?.current ||
      (currentStepData.targetSelector
        ? (document.querySelector(currentStepData.targetSelector) as HTMLElement | null)
        : null);

    if (!el) {
      setTargetRect(null);
      return;
    }
    const rect = el.getBoundingClientRect();
    // An element that is hidden or collapsed is not worth pointing at.
    if (rect.width < 2 || rect.height < 2) {
      setTargetRect(null);
      return;
    }
    setTargetRect(rect);
  }, [isVisible, currentStepData]);

  useLayoutEffect(() => {
    measureTarget();
    // The menu opening/closing animates, so re-measure shortly after.
    const t = window.setTimeout(measureTarget, 260);
    return () => window.clearTimeout(t);
  }, [measureTarget]);

  useEffect(() => {
    if (!isVisible) return;
    const onChange = () => measureTarget();
    window.addEventListener('resize', onChange);
    window.addEventListener('scroll', onChange, true);
    return () => {
      window.removeEventListener('resize', onChange);
      window.removeEventListener('scroll', onChange, true);
    };
  }, [isVisible, measureTarget]);

  // Track the card's own size so placement can avoid running off screen.
  useLayoutEffect(() => {
    if (!isVisible || !cardRef.current) return;
    const el = cardRef.current;
    const update = () => {
      const r = el.getBoundingClientRect();
      setCardSize(prev =>
        Math.abs(prev.height - r.height) > 1 || Math.abs(prev.width - r.width) > 1
          ? { width: r.width, height: r.height }
          : prev
      );
    };
    update();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [isVisible, currentStep]);

  /**
   * Place the card on the preferred side of the target, flipping to the
   * opposite side (and finally to center) when it would not fit on screen.
   */
  const placement: CardPlacement = useMemo(() => {
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 768;
    const { width: cw, height: ch } = cardSize;

    if (!targetRect) {
      return { top: Math.max(CARD_MARGIN, (vh - ch) / 2), left: Math.max(CARD_MARGIN, (vw - cw) / 2), side: 'center' };
    }

    const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);
    const fits = {
      top: targetRect.top - ch - CARD_MARGIN >= CARD_MARGIN,
      bottom: targetRect.bottom + ch + CARD_MARGIN <= vh - CARD_MARGIN,
      left: targetRect.left - cw - CARD_MARGIN >= CARD_MARGIN,
      right: targetRect.right + cw + CARD_MARGIN <= vw - CARD_MARGIN,
    };

    const preferred = currentStepData?.position || 'top';
    const order: Array<'top' | 'bottom' | 'left' | 'right'> = [
      preferred,
      preferred === 'top' ? 'bottom' : preferred === 'bottom' ? 'top' : preferred === 'left' ? 'right' : 'left',
      'top',
      'bottom',
      'right',
      'left',
    ];
    const side = order.find(s => fits[s]);

    if (!side) {
      return { top: clamp((vh - ch) / 2, CARD_MARGIN, vh - ch - CARD_MARGIN), left: clamp((vw - cw) / 2, CARD_MARGIN, vw - cw - CARD_MARGIN), side: 'center' };
    }

    const centerX = clamp(targetRect.left + targetRect.width / 2 - cw / 2, CARD_MARGIN, Math.max(CARD_MARGIN, vw - cw - CARD_MARGIN));
    const centerY = clamp(targetRect.top + targetRect.height / 2 - ch / 2, CARD_MARGIN, Math.max(CARD_MARGIN, vh - ch - CARD_MARGIN));

    switch (side) {
      case 'top':
        return { top: targetRect.top - ch - CARD_MARGIN, left: centerX, side };
      case 'bottom':
        return { top: targetRect.bottom + CARD_MARGIN, left: centerX, side };
      case 'left':
        return { top: centerY, left: targetRect.left - cw - CARD_MARGIN, side };
      default:
        return { top: centerY, left: targetRect.right + CARD_MARGIN, side };
    }
  }, [targetRect, cardSize, currentStepData]);

  // Handle close — reads the latest dontShowAgain via a ref-free closure by
  // being recreated on every change of its inputs.
  const handleClose = useCallback(() => {
    if (dontShowAgain) {
      setTourDismissed(true, storageKey);
    }
    onCloseMenu?.(); // Ensure menu is closed
    setCurrentStep(0);
    onClose();
    onComplete?.();
  }, [dontShowAgain, storageKey, onClose, onCloseMenu, onComplete]);

  const handleNext = useCallback(() => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleClose();
    }
  }, [currentStep, tourSteps.length, handleClose]);

  const handlePrev = useCallback(() => {
    setCurrentStep(prev => (prev > 0 ? prev - 1 : prev));
  }, []);

  // Keyboard navigation, scoped to the tour so it cannot hijack typing
  // elsewhere in the app.
  useEffect(() => {
    if (!isVisible) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrev();
      } else if (e.key === 'Tab' && cardRef.current) {
        // Keep focus inside the dialog.
        const focusables = cardRef.current.querySelectorAll<HTMLElement>(
          'button, input, a[href], [tabindex]:not([tabindex="-1"])'
        );
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey && (active === first || !cardRef.current.contains(active))) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, handleClose, handleNext, handlePrev]);

  // Move focus into the tour on open, and give it back on close.
  useEffect(() => {
    if (!isVisible) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const t = window.setTimeout(() => {
      cardRef.current?.querySelector<HTMLElement>('[data-tour-primary]')?.focus();
    }, 40);
    return () => {
      window.clearTimeout(t);
      previouslyFocused.current?.focus?.();
    };
  }, [isVisible]);

  if (!isVisible || !currentStepData) return null;

  // Theme colors
  const progressPercent = tourSteps.length > 0 ? ((currentStep + 1) / tourSteps.length) * 100 : 100;
  // MediaSFU design system: popover surface, hairline, teal accent, and the
  // teal-to-sky gradient only on the primary (Next) action and the icon tile.
  const brand = brandSurface(isDarkMode);
  const bgColor = brand.popover;
  const textColor = brand.text;
  const textSecondary = brand.soft;
  const borderColor = brand.line;
  const primaryColor = isDarkMode ? MEDIASFU_BRAND.teal : MEDIASFU_BRAND.tealText;
  const accentGradient = MEDIASFU_BRAND.gradient;

  const spotlight = targetRect
    ? {
        top: targetRect.top - SPOTLIGHT_PADDING,
        left: targetRect.left - SPOTLIGHT_PADDING,
        width: targetRect.width + SPOTLIGHT_PADDING * 2,
        height: targetRect.height + SPOTLIGHT_PADDING * 2,
      }
    : null;

  const scrimColor = isDarkMode ? 'rgba(2, 6, 23, 0.62)' : 'rgba(15, 23, 42, 0.45)';

  return (
    <>
      {/* Scrim. With a target we punch a hole using a huge outward shadow on a
          transparent box, which keeps the highlighted control fully readable
          and clickable-looking without a canvas or SVG mask. */}
      {spotlight ? (
        <>
          <div
            style={{
              position: 'fixed',
              top: spotlight.top,
              left: spotlight.left,
              width: spotlight.width,
              height: spotlight.height,
              borderRadius: 14,
              boxShadow: `0 0 0 9999px ${scrimColor}, 0 0 0 2px ${primaryColor}, 0 0 22px 4px ${primaryColor}66`,
              zIndex: 9998,
              pointerEvents: 'none',
              transition: 'top 0.28s ease, left 0.28s ease, width 0.28s ease, height 0.28s ease',
              animation: 'miniTourPulse 2.2s ease-in-out infinite',
            }}
          />
          {/* Separate click-catcher so clicking the dimmed area closes the tour
              while the ring itself stays purely decorative. */}
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 9997 }}
            onClick={handleClose}
            aria-hidden="true"
          />
        </>
      ) : (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: scrimColor,
            backdropFilter: 'blur(2px)',
            WebkitBackdropFilter: 'blur(2px)',
            zIndex: 9998,
            animation: 'miniTourFadeIn 0.3s ease-out',
          }}
          onClick={handleClose}
          aria-hidden="true"
        />
      )}

      {/* Tour card */}
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mini-tour-title"
        aria-describedby="mini-tour-body"
        style={{
          position: 'fixed',
          top: placement.top,
          left: placement.left,
          zIndex: 9999,
          width: `min(${CARD_WIDTH}px, calc(100vw - ${CARD_MARGIN * 2}px))`,
          maxHeight: `calc(100vh - ${CARD_MARGIN * 2}px)`,
          overflowY: 'auto',
          backgroundColor: bgColor,
          borderRadius: '16px',
          border: `1px solid ${borderColor}`,
          boxShadow: isDarkMode
            ? '0 32px 64px -24px rgba(0, 0, 0, 0.8), inset 0 1px 0 rgba(255,255,255,0.04)'
            : '0 32px 64px -28px rgba(15, 23, 42, 0.35)',
          transition: 'top 0.28s cubic-bezier(0.34, 1.2, 0.64, 1), left 0.28s cubic-bezier(0.34, 1.2, 0.64, 1)',
          animation: 'miniTourScaleIn 0.28s cubic-bezier(0.34, 1.56, 0.64, 1)',
        }}
      >
        {/* Header with gradient */}
        <div style={{ padding: '16px 18px 4px', position: 'relative' }}>
          <button
            onClick={handleClose}
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              width: '30px',
              height: '30px',
              borderRadius: '10px',
              border: 'none',
              backgroundColor: 'transparent',
              color: brand.muted,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = brand.fillHover;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            aria-label="Close tour"
          >
            <FontAwesomeIcon icon={faTimes} size="sm" />
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingRight: 34 }}>
            <span
              style={{
                width: '38px',
                height: '38px',
                flexShrink: 0,
                borderRadius: '11px',
                background: accentGradient,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <FontAwesomeIcon icon={currentStepData.icon || faLightbulb} color={MEDIASFU_BRAND.ink} />
            </span>
            <h3 id="mini-tour-title" style={{ margin: 0, fontSize: '17px', fontWeight: 700, color: textColor, lineHeight: 1.25, letterSpacing: '-0.015em', fontFamily: "'Space Grotesk', 'Inter', system-ui, sans-serif" }}>
              {currentStepData.title}
            </h3>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
            <span style={{ fontSize: '11.5px', color: brand.muted, fontWeight: 700, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
              {currentStep + 1} / {tourSteps.length}
            </span>
            <span style={{ flex: 1, height: 3, borderRadius: 999, overflow: 'hidden', background: brand.lineStrong }}>
              <span
                style={{
                  display: 'block',
                  width: `${progressPercent}%`,
                  height: '100%',
                  borderRadius: 999,
                  background: MEDIASFU_BRAND.teal,
                  transition: 'width 0.2s ease',
                }}
              />
            </span>
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: '16px 18px 18px' }}>
          <div aria-live="polite">
            <p
              id="mini-tour-body"
              style={{ margin: 0, fontSize: '14px', lineHeight: 1.55, color: textSecondary }}
              dangerouslySetInnerHTML={{ __html: currentStepData.content || '' }}
            />

            {currentStepData.bullets && currentStepData.bullets.length > 0 && (
              <ul style={{ margin: '12px 0 0', padding: 0, listStyle: 'none', display: 'grid', gap: 7 }}>
                {currentStepData.bullets.map(item => (
                  <li
                    key={item}
                    style={{ display: 'flex', gap: 9, alignItems: 'flex-start', fontSize: '13px', lineHeight: 1.45, color: textSecondary }}
                  >
                    <span
                      aria-hidden="true"
                      style={{ width: 5, height: 5, borderRadius: '50%', background: primaryColor, marginTop: 7, flexShrink: 0 }}
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              fontSize: '12.5px',
              color: textSecondary,
              cursor: 'pointer',
              margin: '16px 0 14px',
              padding: '9px 11px',
              borderRadius: '10px',
              border: `1px solid ${borderColor}`,
              backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.03)',
              userSelect: 'none',
            }}
          >
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={e => setDontShowAgain(e.target.checked)}
              style={{ width: '17px', height: '17px', accentColor: primaryColor, cursor: 'pointer', flexShrink: 0 }}
            />
            Don&apos;t show this tour again
          </label>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={handlePrev}
              disabled={currentStep === 0}
              style={{
                flex: 1,
                height: 42,
                borderRadius: '10px',
                border: `1px solid ${borderColor}`,
                backgroundColor: 'transparent',
                color: currentStep === 0 ? textSecondary : textColor,
                fontSize: '14px',
                fontWeight: 600,
                cursor: currentStep === 0 ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                opacity: currentStep === 0 ? 0.45 : 1,
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={e => {
                if (currentStep > 0) {
                  e.currentTarget.style.backgroundColor = isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)';
                }
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <FontAwesomeIcon icon={faArrowLeft} size="sm" />
              Back
            </button>

            <button
              onClick={handleNext}
              data-tour-primary=""
              style={{
                flex: 2,
                height: 42,
                borderRadius: '10px',
                border: 'none',
                background: accentGradient,
                color: MEDIASFU_BRAND.ink,
                fontSize: '14px',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'all 0.2s ease',
                boxShadow: '0 4px 14px rgba(0, 0, 0, 0.18)',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.25)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 14px rgba(0, 0, 0, 0.18)';
              }}
            >
              {isLastStep ? 'Finish' : 'Next'}
              {!isLastStep && <FontAwesomeIcon icon={faArrowRight} size="sm" />}
            </button>
          </div>
        </div>
      </div>

      {/* CSS Animations */}
      <style>
        {`
          @keyframes miniTourFadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes miniTourScaleIn {
            from { opacity: 0; transform: scale(0.96); }
            to { opacity: 1; transform: scale(1); }
          }
          @keyframes miniTourPulse {
            0%, 100% { filter: brightness(1); }
            50% { filter: brightness(1.18); }
          }
          @media (prefers-reduced-motion: reduce) {
            [role="dialog"][aria-labelledby="mini-tour-title"] { animation: none !important; transition: none !important; }
          }
        `}
      </style>
    </>
  );
};

// Export utility functions
export { getTourDismissed, setTourDismissed, TOUR_STORAGE_KEY };

export default MiniTour;
